// Typography utility to apply theme settings globally
export const applyTypographyToDocument = (theme) => {
  if (!theme?.typography) return;
  
  const root = document.documentElement;
  const typo = theme.typography;
  
  // Apply font family
  if (typo.font_family) {
    root.style.setProperty('--font-family', typo.font_family);
    document.body.style.fontFamily = typo.font_family;
    
    // Dynamically load Google Font if it's a Google Font
    loadGoogleFont(typo.font_family);
  }
  
  if (typo.heading_font) {
    root.style.setProperty('--heading-font', typo.heading_font);
    loadGoogleFont(typo.heading_font);
  }
  
  if (typo.body_font) {
    root.style.setProperty('--body-font', typo.body_font);
    loadGoogleFont(typo.body_font);
  }
  
  // Apply font sizes with !important to override any inline styles
  const fontSizeBase = typo.font_size_base || '16px';
  const fontSizeLg = typo.font_size_lg || '18px';
  const fontSizeXl = typo.font_size_xl || '20px';
  const fontFamily = typo.font_family || 'Raleway, sans-serif';
  const headingFont = typo.heading_font || fontFamily;
  
  root.style.setProperty('--font-size-base', fontSizeBase);
  root.style.setProperty('--font-size-lg', fontSizeLg);
  root.style.setProperty('--font-size-xl', fontSizeXl);
  document.body.style.fontSize = fontSizeBase;
  
  // Remove existing dynamic style if present
  const existing = document.getElementById('dynamic-typography');
  if (existing) existing.remove();
  
  // Create comprehensive dynamic stylesheet
  const styleSheet = document.createElement('style');
  styleSheet.id = 'dynamic-typography';
  
  styleSheet.textContent = `
    :root {
      --font-size-base: ${fontSizeBase} !important;
      --font-size-lg: ${fontSizeLg} !important;
      --font-size-xl: ${fontSizeXl} !important;
      --font-family: ${fontFamily} !important;
      --heading-font: ${headingFont} !important;
    }
    
    html, body {
      font-size: ${fontSizeBase} !important;
      font-family: ${fontFamily} !important;
    }
    
    /* Apply to all text elements */
    p, span, div, li, a, label, input, textarea, button, td, th {
      font-family: ${fontFamily} !important;
    }
    
    /* Headings use heading font */
    h1, h2, h3, h4, h5, h6 {
      font-family: ${headingFont} !important;
    }
    
    /* Specific text size classes */
    .text-sm { font-size: calc(${fontSizeBase} * 0.875) !important; }
    .text-base { font-size: ${fontSizeBase} !important; }
    .text-lg { font-size: ${fontSizeLg} !important; }
    .text-xl { font-size: ${fontSizeXl} !important; }
    .text-2xl { font-size: calc(${fontSizeXl} * 1.25) !important; }
    .text-3xl { font-size: calc(${fontSizeXl} * 1.5) !important; }
    .text-4xl { font-size: calc(${fontSizeXl} * 1.875) !important; }
    .text-5xl { font-size: calc(${fontSizeXl} * 2.25) !important; }
    
    /* Card content */
    .card p, .card span, .card div {
      font-family: ${fontFamily} !important;
    }
    
    /* Navigation and buttons inherit font */
    nav, nav a, nav button {
      font-family: ${fontFamily} !important;
    }
    
    /* Footer */
    footer, footer p, footer a, footer span {
      font-family: ${fontFamily} !important;
    }
  `;
  
  document.head.appendChild(styleSheet);
};

// Helper function to load Google Fonts dynamically
const loadGoogleFont = (fontFamily) => {
  if (!fontFamily) return;
  
  // Extract font name (before comma for fallbacks)
  const fontName = fontFamily.split(',')[0].trim().replace(/['"]/g, '');
  
  // Skip system fonts
  const systemFonts = ['sans-serif', 'serif', 'monospace', 'system-ui', '-apple-system', 'BlinkMacSystemFont'];
  if (systemFonts.some(sf => fontName.toLowerCase().includes(sf.toLowerCase()))) return;
  
  // Check if font is already loaded
  const existingLink = document.querySelector(`link[href*="${fontName.replace(/\s+/g, '+')}"]`);
  if (existingLink) return;
  
  // Create Google Fonts link
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
};

// Apply colors from theme
export const applyColorsToDocument = (theme) => {
  if (!theme?.colors) return;
  
  const root = document.documentElement;
  const colors = theme.colors;
  
  if (colors.primary) root.style.setProperty('--color-primary', colors.primary);
  if (colors.secondary) root.style.setProperty('--color-secondary', colors.secondary);
  if (colors.accent) root.style.setProperty('--color-accent', colors.accent);
  if (colors.background) root.style.setProperty('--color-background', colors.background);
  if (colors.text_primary) root.style.setProperty('--color-text-primary', colors.text_primary);
  if (colors.text_secondary) root.style.setProperty('--color-text-secondary', colors.text_secondary);
};

// Main function to apply all theme settings
export const applyThemeToDocument = (settings) => {
  if (!settings?.theme) return;
  
  applyTypographyToDocument(settings.theme);
  applyColorsToDocument(settings.theme);
  
  // Apply design tokens
  const root = document.documentElement;
  if (settings.theme.border_radius) {
    root.style.setProperty('--border-radius', settings.theme.border_radius);
  }
  if (settings.theme.section_spacing) {
    root.style.setProperty('--section-spacing', settings.theme.section_spacing);
  }
};
